import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { profilesAPI, jarvisAPI, forumsAPI, paymentsAPI } from '../api/client';
import type { ResumeExtractFields } from '../api/client';
import { useAuth } from '../context/useAuth';
import TagPicker from '../components/TagPicker';
import AvatarCapture from '../components/AvatarCapture';
import JarvisFieldAssist from '../components/JarvisFieldAssist';
import MicButton from '../components/MicButton';
import { formatPhone } from '../utils/phone';
import { normalizeUrl } from '../utils/url';
import { COUNTRIES } from '../utils/countries';
import { FIELD_ASSIST } from '../components/fieldAssist';
import { useLinkedInConnect } from '../hooks/useLinkedInConnect';
import { isNativePlatform } from '../native';
import { routeAfterOnboarding, shouldExposeWebCheckout } from '../lib/paymentGate';
import {
  User as UserIcon,
  Briefcase,
  MapPin,
  Heart,
  Target,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Loader2,
  Building2,
  FileSignature,
  Phone,
  Globe,
  Home,
  Linkedin,
  HelpCircle,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type {
  UserProfile,
  MembershipRegistration,
  ForumListItem,
  ForumType,
  ProfileCompletionRequirement,
} from '../types';

// Labels for the resume/LinkedIn-text extractor's suggested fields (Task B).
const RESUME_FIELD_LABELS: Record<string, string> = {
  bio: 'Bio',
  title: 'Title',
  industry: 'Industry',
  business_type: 'Business Type',
  years_in_business: 'Years in Business',
  hobbies_interests: 'Hobbies & Interests',
  areas_of_expertise: 'Areas of Expertise',
};

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
];

// Business-profiling options requested by Iron Forums (value must match the
// backend choices in users/models.py).
const REVENUE_RANGES = [
  { value: 'under_500k', label: '<$500k' },
  { value: '500k_1m', label: '$500k–$1M' },
  { value: '1m_5m', label: '$1M–$5M' },
  { value: '5m_10m', label: '$5M–$10M' },
  { value: '10m_25m', label: '$10M–$25M' },
  { value: '25m_plus', label: '$25M+' },
];

const GROWTH_RATES = [
  { value: 'flat_declining', label: 'Flat / Declining' },
  { value: '1_10', label: '1–10%' },
  { value: '10_25', label: '10–25%' },
  { value: '25_plus', label: '25%+' },
];

const COMPANY_STRUCTURES = ['Solopreneur', 'LLC', 'S-Corp', 'C-Corp', 'Franchise', 'Family-Owned', 'Nonprofit'];

const POSITIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' },
];

const FORUM_TYPE_LABEL: Record<ForumType, string> = {
  mens: "Men's Forum",
  womens: "Women's Forum",
  mixed: 'Combined Forum',
};

// Format a DRF decimal string ("400.00") as monthly dues ("$400 / month").
function formatForumMonthly(price: string | undefined): string {
  const amount = Number(price);
  if (!price || Number.isNaN(amount)) return '';
  const dollars = Number.isInteger(amount)
    ? `$${amount.toLocaleString()}`
    : `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${dollars} / month`;
}

// The six binding acknowledgements from the Iron Forums registration form.
const AGREEMENTS: { field: keyof MembershipRegistration; label: string; detail: string }[] = [
  {
    field: 'agreed_autopay',
    label: 'Automatic credit card payments',
    detail:
      'I agree to automatic credit card payments of the amount indicated above. I understand that I am required to pay my monthly Iron Forums membership fee even if I miss a monthly Forum.',
  },
  {
    field: 'commit_attend',
    label: 'Commit to attend monthly Forums',
    detail:
      'I commit to attend the monthly Forums and to notify my facilitator if for any reason I am unable to attend.',
  },
  {
    field: 'agree_arrive_on_time',
    label: 'Arrive and leave on time',
    detail:
      'I agree to plan to arrive and leave on time for each monthly Forum so that all members enjoy the mutual benefit of serving and being held accountable to each other, personally and professionally. I will come prepared to participate in my monthly Forum.',
  },
  {
    field: 'agree_confidentiality',
    label: 'Confidentiality',
    detail:
      'I agree to hold all information shared or discussed by another member during our Iron Forum in strict confidence. I understand this confidentiality extends indefinitely and is critical to maintaining the trust and integrity of the group. I acknowledge that any breach, whether intentional or unintentional, will result in the immediate termination of my membership privileges and may also have additional consequences as deemed appropriate by the group.',
  },
  {
    field: 'agree_statement_of_faith',
    label: 'Statement of Faith',
    detail:
      'I agree with and commit to uphold the beliefs found in the Iron Forums Statement of Faith.',
  },
  {
    field: 'agree_healthy_submission',
    label: 'Healthy mutual submission',
    detail:
      'I agree to a healthy mutual submission to the community of Iron Forums as is detailed in Matthew 18:15-17. I understand this is holistic (encompassing my personal, family and professional life) and is part of my commitment to the 4H\u2019s of Iron Forums (Humble, Hungry, Healthy and Holy-Spirit Driven).',
  },
];

const GUEST_STEP_INDEXES = [0, 2, 4, 6, 7];

function adjacentGuestStep(current: number, direction: 1 | -1): number {
  const index = GUEST_STEP_INDEXES.indexOf(current);
  const next = index + direction;
  if (next < 0) return GUEST_STEP_INDEXES[0];
  if (next >= GUEST_STEP_INDEXES.length) return GUEST_STEP_INDEXES[GUEST_STEP_INDEXES.length - 1];
  return GUEST_STEP_INDEXES[next];
}

const STEPS = [
  { icon: UserIcon, title: 'Basics', description: 'Your photo and personal details' },
  { icon: BookOpen, title: 'About You', description: 'Share your story' },
  { icon: Briefcase, title: 'Professional', description: 'Your work and company' },
  { icon: Target, title: 'Expertise & Growth', description: 'Skills and areas to develop' },
  { icon: Heart, title: 'Personal & Family', description: 'Home and family details' },
  { icon: Building2, title: 'Forum & Membership', description: 'Your Forum and membership tier' },
  { icon: FileSignature, title: 'Agreements', description: 'Member commitments & signature' },
  { icon: Check, title: 'Review & Complete', description: 'Finalize your registration' },
];

const FIELD_ERROR_LABELS: Record<string, string> = {
  avatar_url: 'Profile photo',
  first_name: 'First name',
  last_name: 'Last name',
  cell_phone: 'Cell phone',
  home_address: 'Home street address',
  home_city: 'Home city',
  home_state: 'Home state',
  home_country: 'Home country',
  industry: 'Industry',
  num_staff: 'Number of staff',
  website: 'Website',
  spouse_email: 'Spouse email',
  digital_signature: 'Digital signature',
};

const FALLBACK_MEMBER_REQUIREMENTS: ProfileCompletionRequirement[] = [
  { key: 'avatar_url', source: 'profile', label: 'Profile photo', step: 0 },
  { key: 'first_name', source: 'profile', label: 'First name', step: 0 },
  { key: 'last_name', source: 'profile', label: 'Last name', step: 0 },
  { key: 'cell_phone', source: 'profile', label: 'Cell phone', step: 0 },
  { key: 'home_address', source: 'profile', label: 'Home street address', step: 0 },
  { key: 'home_city', source: 'profile', label: 'Home city', step: 0 },
  { key: 'home_state', source: 'profile', label: 'Home state', step: 0 },
  { key: 'home_country', source: 'profile', label: 'Home country', step: 0 },
  { key: 'industry', source: 'profile', label: 'Industry', step: 2 },
  { key: 'num_staff', source: 'profile', label: 'Number of staff', step: 2 },
  { key: 'agreed_autopay', source: 'membership', label: 'Automatic credit card payments', step: 6 },
  { key: 'commit_attend', source: 'membership', label: 'Commit to attend monthly Forums', step: 6 },
  { key: 'agree_arrive_on_time', source: 'membership', label: 'Arrive and leave on time', step: 6 },
  { key: 'agree_confidentiality', source: 'membership', label: 'Confidentiality', step: 6 },
  { key: 'agree_statement_of_faith', source: 'membership', label: 'Statement of Faith', step: 6 },
  { key: 'agree_healthy_submission', source: 'membership', label: 'Healthy mutual submission', step: 6 },
  { key: 'digital_signature', source: 'membership', label: 'Digital signature', step: 6 },
];

const FALLBACK_GUEST_REQUIREMENTS = FALLBACK_MEMBER_REQUIREMENTS.filter((item) => (
  !['avatar_url', 'home_address', 'home_city', 'home_state', 'home_country',
    'agreed_autopay', 'commit_attend', 'agree_arrive_on_time', 'agree_healthy_submission']
    .includes(item.key)
));

function errorMessages(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return [value];
  return [];
}

function registrationError(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
    const data = error.response.data as Record<string, unknown>;
    const parts: string[] = [];
    for (const [key, value] of Object.entries(data)) {
      if (key === 'profile_completion') continue;
      const messages = errorMessages(value);
      if (messages.length === 0) continue;
      if (key === 'detail' || key === 'non_field_errors') {
        parts.push(...messages);
        continue;
      }
      const label = FIELD_ERROR_LABELS[key] || key.replace(/_/g, ' ');
      for (const message of messages) {
        parts.push(message.toLowerCase().includes(label.toLowerCase())
          ? message
          : `${label}: ${message}`);
      }
    }
    if (parts.length > 0) return parts.join(' ');
  }
  return "We couldn't finish your registration. Please try again.";
}

function firstErrorField(error: unknown): string | null {
  if (!axios.isAxiosError(error) || !error.response?.data || typeof error.response.data !== 'object') {
    return null;
  }
  for (const key of Object.keys(error.response.data as Record<string, unknown>)) {
    if (key === 'detail' || key === 'non_field_errors' || key === 'profile_completion') continue;
    return key;
  }
  return null;
}

export default function ProfileOnboarding() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const isGuest = user?.role === 'guest';
  const visibleAgreements = isGuest
    ? AGREEMENTS.filter((item) => (
      item.field === 'agree_confidentiality' || item.field === 'agree_statement_of_faith'
    ))
    : AGREEMENTS;
  const [faithModalOpen, setFaithModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<Partial<UserProfile>>({});
  const [membership, setMembership] = useState<Partial<MembershipRegistration>>({});
  const [completionRequirements, setCompletionRequirements] = useState<
    ProfileCompletionRequirement[]
  >([]);
  // The member's invite-assigned forum drives their (read-only) dues; members
  // don't pick a forum or browse pricing during onboarding.
  const [assignedForum, setAssignedForum] = useState<ForumListItem | null>(null);
  // Server-resolved monthly dues (e.g. "$115/mo") — role- and override-aware,
  // the same amount Stripe will actually charge. The forum's raw member price
  // is wrong for advisors and scholarship invitees.
  const [serverDues, setServerDues] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [assistField, setAssistField] = useState<string | null>(null);
  const [stepError, setStepError] = useState('');
  const [linkedinPhotoUrl, setLinkedinPhotoUrl] = useState<string>('');
  const {
    connect: connectLinkedIn,
    status: linkedinStatus,
    profile: linkedinProfile,
    statusMessage: linkedinStatusMessage,
  } = useLinkedInConnect();
  const [showResumeImport, setShowResumeImport] = useState(false);
  const [resumeText, setResumeText] = useState('');
  const [resumeImporting, setResumeImporting] = useState(false);
  const [resumeError, setResumeError] = useState('');
  const [extracted, setExtracted] = useState<ResumeExtractFields | null>(null);
  const [applyKeys, setApplyKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (!linkedinProfile) return;
    setFormData((previous) => ({
      ...previous,
      first_name: previous.first_name || linkedinProfile.first_name,
      last_name: previous.last_name || linkedinProfile.last_name,
    }));
    if (linkedinProfile.picture) setLinkedinPhotoUrl(linkedinProfile.picture);
  }, [linkedinProfile]);

  const loadProfile = async () => {
    try {
      const [data, membershipData, myForums, payment] = await Promise.all([
        profilesAPI.getMyProfile(),
        profilesAPI.getMyMembership().catch(() => ({} as MembershipRegistration)),
        forumsAPI.getMyForums().catch(() => [] as ForumListItem[]),
        paymentsAPI.getStatus().catch(() => null),
      ]);
      setFormData(data);
      setCompletionRequirements(data.profile_completion?.requirements ?? []);
      if (membershipData) setMembership(membershipData);
      if (myForums && myForums.length > 0) setAssignedForum(myForums[0]);
      if (payment?.forum_price) setServerDues(payment.forum_price);
    } catch {
      // Use auth user as fallback
      if (user) setFormData(user as Partial<UserProfile>);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    await profilesAPI.updateMyProfile({ ...formData, website: normalizeUrl(formData.website) });
  };

  const saveMembership = async () => {
    if (Object.keys(membership).length === 0) return;
    const updated = await profilesAPI.updateMyMembership(membership);
    setMembership(updated);
  };

  // Returns false if the save failed so the caller can keep the member on the
  // current step instead of advancing past data that never persisted.
  const saveStep = async () => {
    setIsSaving(true);
    try {
      await Promise.all([saveProfile(), saveMembership()]);
      return true;
    } catch (error) {
      toast.error(registrationError(error), { duration: 8000 });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // The dues shown must match what Stripe charges: prefer the server-resolved
  // price; fall back to the forum's role-appropriate rate if that call failed.
  const monthlyDuesLabel = serverDues
    ? serverDues.replace(/\/mo$/, ' / month')
    : formatForumMonthly(
        user?.role === 'advisor'
          ? assignedForum?.advisor_monthly_price
          : assignedForum?.monthly_price,
      );

  const isRequirementMissing = ({ key, source }: ProfileCompletionRequirement) => {
    const values = source === 'profile' ? formData : membership;
    const value = (values as Record<string, unknown>)[key];
    if (typeof value === 'string') return !value.trim();
    if (typeof value === 'boolean') return !value;
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === null;
  };

  const missingForStep = (targetStep: number) =>
    completionRequirements
      .filter((requirement) => requirement.step === targetStep)
      .filter(isRequirementMissing);

  const focusRequirement = (requirement: ProfileCompletionRequirement) => {
    setStep(requirement.step);
    window.setTimeout(() => {
      const element = document.getElementById(`${requirement.source}-${requirement.key}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element?.focus({ preventScroll: true });
    }, 0);
  };

  const showMissingRequirements = (missing: ProfileCompletionRequirement[]) => {
    const message = `Please complete: ${missing.map(({ label }) => label).join(', ')}.`;
    setStepError(message);
    toast.error(message, { duration: 8000 });
    focusRequirement(missing[0]);
  };

  const handleNext = async () => {
    const missing = missingForStep(step);
    if (missing.length > 0) {
      showMissingRequirements(missing);
      return;
    }
    setStepError('');
    const saved = await saveStep();
    if (!saved) return;
    if (step < STEPS.length - 1) {
      const next = isGuest ? adjacentGuestStep(step, 1) : step + 1;
      setStep(next);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStepError('');
      const prev = isGuest ? adjacentGuestStep(step, -1) : step - 1;
      setStep(prev);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = async () => {
    // Safety net for the per-step gating (e.g. a member who went Back and
    // cleared a field): jump to the first step that's still incomplete.
    let requirements = completionRequirements;
    try {
      const latest = await profilesAPI.getMyProfile();
      if (latest.profile_completion?.requirements?.length) {
        requirements = latest.profile_completion.requirements;
        setCompletionRequirements(requirements);
      }
    } catch {
      // Use the contract already on the page.
    }
    const contract = requirements.length > 0
      ? requirements
      : (isGuest ? FALLBACK_GUEST_REQUIREMENTS : FALLBACK_MEMBER_REQUIREMENTS);
    const missing = contract.filter(isRequirementMissing);
    if (missing.length > 0) {
      showMissingRequirements(missing);
      return;
    }
    setStepError('');
    setIsSaving(true);
    try {
      await saveMembership();
      await profilesAPI.updateMyProfile({
        ...formData,
        website: normalizeUrl(formData.website),
        profile_completed: true,
        has_seen_onboarding: true,
      } as Partial<UserProfile>);
      if (refreshUser) await refreshUser();
      let canCheckout = false;
      if (!isGuest && shouldExposeWebCheckout(isNativePlatform)) {
        try {
          const payment = await paymentsAPI.getStatus();
          canCheckout = Boolean(payment.can_checkout);
        } catch {
          // Profile completion succeeded. Billing remains recoverable from Settings.
        }
      }
      navigate(routeAfterOnboarding({ isGuest, isNative: isNativePlatform, canCheckout }));
    } catch (error) {
      toast.error(registrationError(error), { duration: 8000 });
      const field = firstErrorField(error);
      const requirement = contract.find((item) => item.key === field)
        || FALLBACK_MEMBER_REQUIREMENTS.find((item) => item.key === field);
      if (requirement) {
        setStepError(`Please complete: ${requirement.label}.`);
        focusRequirement(requirement);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const runResumeExtract = async () => {
    if (!resumeText.trim()) return;
    setResumeImporting(true);
    setResumeError('');
    setExtracted(null);
    try {
      const { fields } = await jarvisAPI.extractFromResume(resumeText);
      const keys = Object.keys(fields);
      if (keys.length === 0) {
        setResumeError(
          "Couldn't pull structured details from that text. Try pasting more, or fill in the fields manually.",
        );
        return;
      }
      // Default to applying only the fields the member hasn't already filled in.
      const defaults = new Set(
        keys.filter((k) => {
          const v = (formData as Record<string, unknown>)[k];
          return (
            v === undefined ||
            v === null ||
            v === '' ||
            (Array.isArray(v) && v.length === 0)
          );
        }),
      );
      setExtracted(fields);
      setApplyKeys(defaults);
    } catch {
      setResumeError('Something went wrong. Please try again or enter details manually.');
    } finally {
      setResumeImporting(false);
    }
  };

  const toggleApplyKey = (key: string) => {
    setApplyKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const applyExtracted = () => {
    if (!extracted) return;
    setFormData((prev) => {
      const next = { ...prev };
      for (const key of applyKeys) {
        const value = (extracted as Record<string, unknown>)[key];
        if (value === undefined) continue;
        if (key === 'areas_of_expertise' && Array.isArray(value)) {
          const existing = prev.areas_of_expertise || [];
          next.areas_of_expertise = Array.from(new Set([...existing, ...(value as string[])]));
        } else {
          (next as Record<string, unknown>)[key] = value;
        }
      }
      return next;
    });
    setExtracted(null);
    setResumeText('');
    setShowResumeImport(false);
  };

  const updateField = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateMembership = (field: keyof MembershipRegistration, value: unknown) => {
    setMembership(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
      </div>
    );
  }

  const visibleStepIndexes = isGuest ? GUEST_STEP_INDEXES : STEPS.map((_, i) => i);
  const progressPercent = ((visibleStepIndexes.indexOf(step) + 1) / visibleStepIndexes.length) * 100;

  const inputClass =
    'w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20';
  const activeRequirements = completionRequirements.length > 0
    ? completionRequirements
    : (isGuest ? FALLBACK_GUEST_REQUIREMENTS : FALLBACK_MEMBER_REQUIREMENTS);
  const missingRequirements = activeRequirements.filter(isRequirementMissing);

  // Label row for a free-text field: a hover hint with guiding prompts, plus an
  // optional "Help from Jarvis" trigger. Members can always just type instead.
  const AssistLabel = ({ field, label }: { field: string; label: string }) => {
    const cfg = FIELD_ASSIST[field];
    return (
      <div className="flex items-center justify-between mb-1">
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          {label}
          {cfg && (
            <span title={cfg.hint} className="text-gray-400 cursor-help">
              <HelpCircle size={14} />
            </span>
          )}
        </label>
        {cfg && (
          <button
            type="button"
            onClick={() => setAssistField(field)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gold hover:text-gold-dark transition"
            title="Ask IF asks a few questions, then drafts this from your answers"
          >
            <Sparkles size={14} />
            Help from Ask IF
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-off-white max-lg:flex max-lg:h-full max-lg:min-h-0 max-lg:flex-col max-lg:overflow-hidden lg:min-h-screen">
      {/* Header */}
      <div className="shrink-0 bg-white shadow-sm pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-navy">
            {isGuest ? 'Complete your guest profile' : 'Complete Your Registration'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isGuest
              ? 'A short guest profile: basics, work, family, confidentiality, and our Statement of Faith.'
              : 'Tell us about yourself and confirm your membership commitments'}
          </p>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-navy transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="min-h-0 max-lg:flex-1 max-lg:overflow-y-auto max-lg:overscroll-y-contain">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center flex-wrap gap-2 mb-8">
          {STEPS.map((s, i) => {
            if (isGuest && !GUEST_STEP_INDEXES.includes(i)) return null;
            const Icon = s.icon;
            return (
              <button
                key={i}
                onClick={() => { if (i <= step) { setStepError(''); setStep(i); } }}
                className={`flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 rounded-full text-xs font-medium transition ${
                  i === step
                    ? 'bg-navy text-white'
                    : i < step
                    ? 'bg-navy/10 text-navy cursor-pointer'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{s.title}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            {(() => { const Icon = STEPS[step].icon; return <Icon size={20} />; })()}
            {STEPS[step].title}
          </h2>
          <p className="text-sm text-gray-500 mb-6">{STEPS[step].description}</p>

          {/* Step 0: Basics (personal) */}
          {step === 0 && (
            <div className="space-y-5">
              {!isGuest && (
              <>
              {/* Import from resume / LinkedIn text — optional AI pre-fill. */}
              <div className="rounded-xl border border-gold/30 bg-gold/5 p-4">
                <button
                  type="button"
                  onClick={() => setShowResumeImport((v) => !v)}
                  className="w-full flex items-center justify-between gap-3"
                >
                  <span className="flex items-center gap-2">
                    <FileText size={18} className="text-gold" />
                    <span className="text-sm font-medium text-navy">
                      Import from resume / LinkedIn text
                    </span>
                  </span>
                  {showResumeImport ? (
                    <ChevronUp size={16} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={16} className="text-gray-400" />
                  )}
                </button>
                {!showResumeImport && (
                  <p className="text-xs text-gray-600 mt-2">
                    Optional: paste your resume or LinkedIn "About" section and Ask IF will suggest
                    fields to pre-fill. You review everything before it's applied.
                  </p>
                )}
                {showResumeImport && (
                  <div className="mt-3 space-y-3">
                    <textarea
                      value={resumeText}
                      onChange={(e) => setResumeText(e.target.value)}
                      rows={6}
                      placeholder="Paste your resume or LinkedIn About section..."
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={runResumeExtract}
                      disabled={resumeImporting || !resumeText.trim()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-gold text-white hover:bg-gold-dark transition disabled:opacity-60"
                    >
                      {resumeImporting ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Sparkles size={15} />
                      )}
                      {resumeImporting ? 'Reading...' : 'Generate suggestions'}
                    </button>
                    {resumeError && <p className="text-xs text-red-500">{resumeError}</p>}

                    {extracted && (
                      <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                        <p className="text-xs text-gray-500">
                          Review what Ask IF found. Unchecked items won't be applied, and you can
                          still edit everything afterward.
                        </p>
                        {Object.keys(extracted).map((key) => {
                          const value = (extracted as Record<string, unknown>)[key];
                          const display = Array.isArray(value) ? value.join(', ') : String(value);
                          return (
                            <label key={key} className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={applyKeys.has(key)}
                                onChange={() => toggleApplyKey(key)}
                                className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-navy focus:ring-navy/30"
                              />
                              <span className="text-sm">
                                <span className="font-medium text-navy">
                                  {RESUME_FIELD_LABELS[key] || key}:{' '}
                                </span>
                                <span className="text-gray-700">{display}</span>
                              </span>
                            </label>
                          );
                        })}
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={applyExtracted}
                            disabled={applyKeys.size === 0}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-navy text-white hover:bg-navy-dark transition disabled:opacity-60"
                          >
                            <Check size={15} />
                            Apply selected
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Connect LinkedIn — ask early so it can pre-fill what it can. */}
              <div className="rounded-xl border border-[#0a66c2]/25 bg-[#0a66c2]/5 p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Linkedin size={18} className="text-[#0a66c2]" />
                    <span className="text-sm font-medium text-navy">Connect LinkedIn</span>
                  </div>
                  <button
                    type="button"
                    onClick={connectLinkedIn}
                    disabled={linkedinStatus === 'connecting' || linkedinStatus === 'connected'}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-[#0a66c2] text-white hover:bg-[#085397] transition disabled:opacity-60"
                  >
                    {linkedinStatus === 'connecting' ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Linkedin size={15} />
                    )}
                    {linkedinStatus === 'connected' ? 'Connected' : 'Connect'}
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  {linkedinStatusMessage}
                </p>
              </div>
              </>
              )}

              {/* Profile photo (required-ish: directory is photo-based) */}
              <div id="profile-avatar_url" tabIndex={-1}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Photo {!isGuest && <span className="text-red-500">*</span>}
                </label>
                <AvatarCapture
                  avatarUrl={formData.avatar_url}
                  onChange={(url) => updateField('avatar_url', url)}
                  linkedinPhotoUrl={linkedinPhotoUrl}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="profile-first_name" className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input
                    id="profile-first_name"
                    type="text"
                    value={formData.first_name || ''}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="profile-last_name" className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input
                    id="profile-last_name"
                    type="text"
                    value={formData.last_name || ''}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="profile-cell_phone" className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone size={14} className="inline mr-1" />
                    Cell <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="profile-cell_phone"
                    type="tel"
                    inputMode="numeric"
                    value={formData.cell_phone || ''}
                    onChange={(e) => updateField('cell_phone', formatPhone(e.target.value))}
                    placeholder="123-456-7890"
                    maxLength={12}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    disabled
                    className={`${inputClass} bg-gray-50 text-gray-500`}
                  />
                </div>
              </div>

              {/* Home location (personal) */}
              {!isGuest && (
              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
                  <Home size={15} /> Home Location
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="profile-home_address" className="block text-sm font-medium text-gray-700 mb-1">
                      Street address <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="profile-home_address"
                      type="text"
                      value={formData.home_address || ''}
                      onChange={(e) => updateField('home_address', e.target.value)}
                      placeholder="House number and street"
                      autoComplete="street-address"
                      className={inputClass}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="profile-home_city" className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
                      <input
                        id="profile-home_city"
                        type="text"
                        value={formData.home_city || ''}
                        onChange={(e) => updateField('home_city', e.target.value)}
                        placeholder="City"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="profile-home_state" className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                      <select
                        id="profile-home_state"
                        value={formData.home_state || ''}
                        onChange={(e) => updateField('home_state', e.target.value)}
                        className={inputClass}
                      >
                        <option value="">State...</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="home-zip" className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP / Postal Code <span className="font-normal text-gray-500">(optional)</span>
                      </label>
                      <input
                        id="home-zip"
                        type="text"
                        value={formData.home_zip || ''}
                        onChange={(e) => updateField('home_zip', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="profile-home_country" className="block text-sm font-medium text-gray-700 mb-1">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="profile-home_country"
                        value={formData.home_country || ''}
                        onChange={(e) => updateField('home_country', e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select country...</option>
                        {formData.home_country && !COUNTRIES.includes(formData.home_country) && (
                          <option value={formData.home_country}>{formData.home_country}</option>
                        )}
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Spouse (personal) */}
              {!isGuest && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spouse Name</label>
                  <input
                    type="text"
                    value={formData.spouse_name || ''}
                    onChange={(e) => updateField('spouse_name', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spouse Email</label>
                  <input
                    type="email"
                    value={formData.spouse_email || ''}
                    onChange={(e) => updateField('spouse_email', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              )}
            </div>
          )}

          {/* Step 1: About You */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <AssistLabel field="bio" label="Bio" />
                <div className="relative">
                  <textarea
                    value={formData.bio || ''}
                    onChange={(e) => updateField('bio', e.target.value)}
                    rows={4}
                    placeholder="Tell other members about yourself, or use Help from Ask IF..."
                    className={`${inputClass} pr-12`}
                  />
                  <MicButton
                    value={formData.bio || ''}
                    onChange={(next) => updateField('bio', next)}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
              <div>
                <AssistLabel field="faith_journey" label="Faith Journey" />
                <div className="relative">
                  <textarea
                    value={formData.faith_journey || ''}
                    onChange={(e) => updateField('faith_journey', e.target.value)}
                    rows={3}
                    placeholder="Share your faith journey..."
                    className={`${inputClass} pr-12`}
                  />
                  <MicButton
                    value={formData.faith_journey || ''}
                    onChange={(next) => updateField('faith_journey', next)}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
              <div>
                <AssistLabel
                  field="why_iron_forums"
                  label="Why are you seeking Christian business community?"
                />
                <div className="relative">
                  <textarea
                    value={formData.why_iron_forums || ''}
                    onChange={(e) => updateField('why_iron_forums', e.target.value)}
                    rows={3}
                    placeholder="What are you looking for in a community like this?"
                    className={`${inputClass} pr-12`}
                  />
                  <MicButton
                    value={formData.why_iron_forums || ''}
                    onChange={(next) => updateField('why_iron_forums', next)}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Professional */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="e.g., CEO, Founder"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                  <select
                    value={formData.position || ''}
                    onChange={(e) => updateField('position', e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select...</option>
                    {POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Globe size={14} className="inline mr-1" />
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="https://"
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="profile-industry" className="block text-sm font-medium text-gray-700 mb-1">Industry <span className="text-red-500">*</span></label>
                  <input
                    id="profile-industry"
                    type="text"
                    value={formData.industry || ''}
                    onChange={(e) => updateField('industry', e.target.value)}
                    placeholder="e.g., Technology, Construction"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                  <input
                    type="text"
                    value={formData.business_type || ''}
                    onChange={(e) => updateField('business_type', e.target.value)}
                    placeholder="e.g., SaaS, General Contractor"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years in Business</label>
                  <input
                    type="number"
                    value={formData.years_in_business ?? ''}
                    onChange={(e) => updateField('years_in_business', e.target.value ? parseInt(e.target.value) : null)}
                    min={0}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="profile-num_staff" className="block text-sm font-medium text-gray-700 mb-1">Number of Staff <span className="text-red-500">*</span></label>
                  <input
                    id="profile-num_staff"
                    type="number"
                    value={formData.num_staff ?? ''}
                    onChange={(e) => updateField('num_staff', e.target.value ? parseInt(e.target.value) : null)}
                    min={0}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age of Business</label>
                  <select
                    value={formData.business_age || ''}
                    onChange={(e) => updateField('business_age', e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select...</option>
                    <option value="startup">Startup</option>
                    <option value="legacy">Legacy</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Top-Line Revenue Range</label>
                  <select
                    value={formData.revenue_range || ''}
                    onChange={(e) => updateField('revenue_range', e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select...</option>
                    {REVENUE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual Growth Rate</label>
                <select
                  value={formData.growth_rate || ''}
                  onChange={(e) => updateField('growth_rate', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  {GROWTH_RATES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Structure
                  <span className="font-normal text-gray-500 ml-1">(select all that apply)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {COMPANY_STRUCTURES.map((opt) => {
                    const selected = (formData.company_structure || []).includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const current = formData.company_structure || [];
                          updateField(
                            'company_structure',
                            selected ? current.filter((c) => c !== opt) : [...current, opt],
                          );
                        }}
                        className={`tap-target px-3 py-2 rounded-full text-sm font-medium border transition ${
                          selected
                            ? 'bg-navy text-white border-navy'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-navy/40'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Business address */}
              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
                  <Building2 size={15} /> Business Address
                </h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    value={formData.business_address || ''}
                    onChange={(e) => updateField('business_address', e.target.value)}
                    placeholder="Street address"
                    className={inputClass}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={formData.business_city || ''}
                      onChange={(e) => updateField('business_city', e.target.value)}
                      placeholder="City"
                      className={inputClass}
                    />
                    <select
                      value={formData.business_state || ''}
                      onChange={(e) => updateField('business_state', e.target.value)}
                      className={inputClass}
                    >
                      <option value="">State...</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={formData.business_zip || ''}
                      onChange={(e) => updateField('business_zip', e.target.value)}
                      placeholder="ZIP / Postal Code"
                      className={inputClass}
                    />
                    <select
                      value={formData.business_country || ''}
                      onChange={(e) => updateField('business_country', e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select country...</option>
                      {formData.business_country && !COUNTRIES.includes(formData.business_country) && (
                        <option value={formData.business_country}>{formData.business_country}</option>
                      )}
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <AssistLabel field="purpose_statement" label="Purpose Statement" />
                <div className="relative">
                  <textarea
                    value={formData.purpose_statement || ''}
                    onChange={(e) => updateField('purpose_statement', e.target.value)}
                    rows={3}
                    placeholder="What drives your professional mission?"
                    className={`${inputClass} pr-12`}
                  />
                  <MicButton
                    value={formData.purpose_statement || ''}
                    onChange={(next) => updateField('purpose_statement', next)}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Season Goal</label>
                <div className="relative">
                  <textarea
                    value={formData.quarterly_goal || ''}
                    onChange={(e) => updateField('quarterly_goal', e.target.value)}
                    rows={2}
                    placeholder="What are you working toward this season?"
                    className={`${inputClass} pr-12`}
                  />
                  <MicButton
                    value={formData.quarterly_goal || ''}
                    onChange={(next) => updateField('quarterly_goal', next)}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Expertise & Growth */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Areas of Expertise
                  <span className="font-normal text-gray-500 ml-1">(skills you can help others with)</span>
                </label>
                <TagPicker
                  selectedTags={formData.areas_of_expertise || []}
                  onChange={(tags) => updateField('areas_of_expertise', tags)}
                  placeholder="Search expertise tags or type to add..."
                  accentColor="navy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Growth Areas
                  <span className="font-normal text-gray-500 ml-1">(areas you're working to improve)</span>
                </label>
                <TagPicker
                  selectedTags={formData.growth_areas || []}
                  onChange={(tags) => updateField('growth_areas', tags)}
                  placeholder="Search growth area tags or type to add..."
                  accentColor="gold"
                />
              </div>
            </div>
          )}

          {/* Step 4: Personal & Family */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <label htmlFor="profile-family_status" className="block text-sm font-medium text-gray-700 mb-1">
                  Family Status <span className="text-red-500">*</span>
                </label>
                <select
                  id="profile-family_status"
                  value={formData.family_status || ''}
                  onChange={(e) => updateField('family_status', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-1">
                  Do you have children? <span className="text-red-500">*</span>
                </p>
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Do you have children?">
                  {([
                    { label: 'Yes', selected: !!formData.has_children },
                    { label: 'No', selected: !formData.has_children && formData.num_children === 0 },
                  ]).map(({ label, selected }) => (
                    <button
                      key={label}
                      id={label === 'Yes' && !formData.has_children ? 'profile-num_children' : undefined}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        if (label === 'Yes') {
                          updateField('has_children', true);
                          if (formData.num_children === 0) updateField('num_children', null);
                        } else {
                          updateField('has_children', false);
                          updateField('num_children', 0);
                        }
                      }}
                      className={`px-3 py-2 rounded-lg border text-sm transition ${
                        selected
                          ? 'border-navy bg-navy/5 text-navy font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {formData.has_children && (
                <div>
                  <label htmlFor="profile-num_children" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Children <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="profile-num_children"
                    type="number"
                    value={formData.num_children ?? ''}
                    onChange={(e) => updateField('num_children', e.target.value ? parseInt(e.target.value) : null)}
                    min={1}
                    className={inputClass}
                  />
                </div>
              )}

              {!isGuest && (
                <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Church Attending</label>
                <input
                  type="text"
                  value={formData.church_attending || ''}
                  onChange={(e) => updateField('church_attending', e.target.value)}
                  placeholder="Name of the church you attend"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hobbies & Interests</label>
                <div className="relative">
                  <textarea
                    value={formData.hobbies_interests || ''}
                    onChange={(e) => updateField('hobbies_interests', e.target.value)}
                    rows={3}
                    placeholder="What do you enjoy outside of work?"
                    className={`${inputClass} pr-12`}
                  />
                  <MicButton
                    value={formData.hobbies_interests || ''}
                    onChange={(next) => updateField('hobbies_interests', next)}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
                </>
              )}
            </div>
          )}

          {/* Step 5: Forum & Membership — the forum (and therefore the price) is
              assigned by the inviting facilitator/admin, so it's shown read-only
              here rather than chosen or browsed by the member. */}
          {step === 5 && (
            <div className="space-y-6">
              {assignedForum ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 size={14} className="inline mr-1" />
                    Your Forum
                  </label>
                  <div className="rounded-xl border border-navy/20 bg-navy/5 px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-navy">{assignedForum.name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin size={13} />
                          {assignedForum.city}, {assignedForum.state}
                        </p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white text-navy border border-navy/20">
                        {FORUM_TYPE_LABEL[assignedForum.forum_type]}
                      </span>
                    </div>
                    {!isGuest && (
                      <div className="mt-3 pt-3 border-t border-navy/10 flex items-center justify-between">
                        <span className="text-sm text-gray-600">Monthly membership dues</span>
                        <span className="font-semibold text-navy">
                          {monthlyDuesLabel}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {isGuest
                      ? 'You are visiting as a guest of this Forum. There are no membership dues yet.'
                      : 'Your Forum and dues were set by the facilitator who invited you. You\'ll confirm the member agreements next, then activate your membership.'}
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
                  <p className="text-sm font-medium text-navy">Forum assignment pending</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Your facilitator will assign your Forum. Your monthly dues are set by that
                    Forum and will appear here once you're placed — there's nothing to choose now.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Agreements & Signature */}
          {step === 6 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {isGuest
                  ? 'Please agree to confidentiality and acknowledge the Statement of Faith so you can sit in a Forum.'
                  : 'Please review and agree to the following member commitments. These uphold the trust and integrity of every Iron Forum. All six agreements and your signature are required to complete registration.'}
              </p>
              <div className="space-y-3">
                {visibleAgreements.map(({ field, label, detail }) => {
                  const checked = Boolean(membership[field]);
                  const inputId = `membership-${field}`;
                  const descriptionId = `${inputId}-description`;
                  return (
                    <div
                      key={field}
                      className={`flex items-start gap-3 p-3 rounded-xl border transition ${
                        checked ? 'bg-navy/5 border-navy/40' : 'bg-white border-gray-200 hover:border-navy/30'
                      }`}
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => updateMembership(field, e.target.checked)}
                        aria-describedby={descriptionId}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-navy focus:ring-navy/30"
                      />
                      <div>
                        <label htmlFor={inputId} className="block text-sm font-medium text-navy cursor-pointer">
                          {label}
                        </label>
                        <p id={descriptionId} className="block text-xs text-gray-500 mt-0.5">
                          {field === 'agree_statement_of_faith' ? (
                            <>
                              {isGuest
                                ? 'I have read and acknowledge the Iron Forums Statement of Faith as a ministry and have no issues with it. '
                                : 'I agree with and commit to uphold the beliefs found in the Iron Forums Statement of Faith. '}
                              <button
                                type="button"
                                onClick={() => setFaithModalOpen(true)}
                                className="text-navy underline hover:text-gold"
                              >
                                Read Statement of Faith
                              </button>
                            </>
                          ) : detail}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 border-t border-gray-100">
                <label htmlFor="membership-digital_signature" className="block text-sm font-medium text-gray-700 mb-1">
                  <FileSignature size={14} className="inline mr-1" />
                  Digital Signature (type your full name) <span className="text-red-500">*</span>
                </label>
                <input
                  id="membership-digital_signature"
                  type="text"
                  value={membership.digital_signature || ''}
                  onChange={(e) => updateMembership('digital_signature', e.target.value)}
                  placeholder="Type your full name"
                  className={inputClass}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isGuest
                    ? 'By typing your name, you agree to confidentiality and acknowledge the Statement of Faith.'
                    : 'By typing your name, you submit this form for registration as a member of Iron Forums.'}
                </p>
              </div>
            </div>
          )}

          {/* Step 7: Review & Complete */}
          {step === 7 && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Here's a summary of your registration. You can go back to any step to make changes.
              </p>
              {missingRequirements.length > 0 ? (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <p className="font-semibold text-red-800">
                    Complete these required items before submitting:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {missingRequirements.map((requirement) => (
                      <li key={`${requirement.source}-${requirement.key}`}>
                        <button
                          type="button"
                          onClick={() => focusRequirement(requirement)}
                          className="text-sm font-medium text-red-700 underline hover:text-red-900"
                        >
                          Add {requirement.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-800">
                  All required registration details are complete.
                </p>
              )}
              {[
                { label: 'Photo', value: formData.avatar_url ? 'Added' : '', ok: Boolean(formData.avatar_url) },
                { label: 'Name', value: `${formData.first_name || ''} ${formData.last_name || ''}`.trim() },
                { label: 'Cell', value: formData.cell_phone, ok: Boolean((formData.cell_phone || '').trim()) },
                { label: 'Title', value: formData.title },
                { label: 'Industry', value: formData.industry, ok: Boolean((formData.industry || '').trim()) },
                {
                  label: 'Staff',
                  value: formData.num_staff != null ? String(formData.num_staff) : '',
                  ok: formData.num_staff != null,
                },
                { label: 'Business Type', value: formData.business_type },
                { label: 'Revenue Range', value: REVENUE_RANGES.find((r) => r.value === formData.revenue_range)?.label },
                { label: 'Growth Rate', value: GROWTH_RATES.find((r) => r.value === formData.growth_rate)?.label },
                { label: 'Company Structure', value: (formData.company_structure || []).join(', ') },
                { label: 'Business Address', value: [formData.business_city, formData.business_state].filter(Boolean).join(', ') },
                ...(!isGuest
                  ? [
                      {
                        label: 'Street',
                        value: formData.home_address,
                        ok: Boolean((formData.home_address || '').trim()),
                      },
                      {
                        label: 'Home City',
                        value: [
                          formData.home_city,
                          formData.home_state,
                          formData.home_zip,
                          formData.home_country,
                        ].filter(Boolean).join(', '),
                        ok: Boolean(
                          (formData.home_city || '').trim()
                          && (formData.home_state || '').trim()
                          && (formData.home_country || '').trim(),
                        ),
                      },
                      { label: 'Church', value: formData.church_attending },
                      { label: 'Expertise', value: (formData.areas_of_expertise || []).join(', ') },
                      { label: 'Monthly Dues', value: monthlyDuesLabel || 'Set by your Forum' },
                    ]
                  : []),
                { label: 'Forum', value: assignedForum ? `${assignedForum.name} (${FORUM_TYPE_LABEL[assignedForum.forum_type]})` : 'Assigned by facilitator' },
                {
                  label: 'Agreements',
                  value: visibleAgreements.every((a) => membership[a.field])
                    ? (isGuest ? 'Confidentiality and Statement of Faith acknowledged' : 'All agreed')
                    : 'Incomplete',
                  ok: visibleAgreements.every((a) => membership[a.field]),
                },
                {
                  label: 'Signature',
                  value: membership.digital_signature,
                  ok: Boolean((membership.digital_signature || '').trim()),
                },
              ].map(({ label, value, ok }: { label: string; value?: string; ok?: boolean }) => {
                const isOk = ok ?? Boolean(value);
                return (
                  <div key={label} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span className="text-sm font-medium text-gray-500 w-32 shrink-0">{label}</span>
                    <span className={`text-sm ${
                      isOk ? 'text-gray-900' : value || ok === false ? 'text-red-600 font-medium' : 'text-gray-400 italic'
                    }`}>
                      {value || 'Not set'}
                    </span>
                    {isOk && <Check size={16} className="text-green-500 shrink-0 ml-auto" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {stepError && (
          <p
            role="alert"
            aria-live="assertive"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
          >
            {stepError} Focus moved to the first missing item.
          </p>
        )}
      </div>
      </div>

      <div className="shrink-0 border-t border-gray-100 bg-off-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <button
            onClick={handleBack}
            disabled={step === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              step === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-navy hover:bg-white'
            }`}
          >
            <ChevronLeft size={16} />
            Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  Next
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 bg-gold text-white rounded-lg text-sm font-medium hover:bg-gold-dark transition disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <Check size={16} />
                  Complete Registration
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {assistField && FIELD_ASSIST[assistField] && (
        <JarvisFieldAssist
          field={assistField}
          title={FIELD_ASSIST[assistField].title}
          intro={FIELD_ASSIST[assistField].intro}
          questions={FIELD_ASSIST[assistField].questions}
          onApply={(draft) => {
            updateField(assistField, draft);
            setAssistField(null);
          }}
          onClose={() => setAssistField(null)}
        />
      )}

      {faithModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-navy">Iron Forums Statement of Faith</h2>
              <button
                type="button"
                onClick={() => setFaithModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close Statement of Faith"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto text-sm text-gray-700 space-y-3 leading-relaxed">
              <p>We affirm that the Bible is the inspired, inerrant, infallible, and authoritative Word of God.</p>
              <p>We affirm the Lord Jesus Christ as the eternal Son of God, and the only Way to salvation.</p>
              <p>We affirm the indwelling of the Holy Spirit in every Christian.</p>
              <p>We affirm God’s design in gender, marriage and sexuality.</p>
              <p>Each person is wonderfully and immutably created as male or female.</p>
              <p>Marriage is a sacred covenant and union between one male and one female.</p>
              <p>Sexuality is a gift to be practiced only within the bond of marriage.</p>
              <p>
                We affirm that all human life, from conception to natural death, is sacred and created
                by God in His image and therefore holds great value, dignity and the right to be
                protected regardless of age, race, gender or national origin.
              </p>
              <p>
                We affirm the universal church as the Body of Christ and our call to strengthen her
                through active involvement in a biblically rooted local church.
              </p>
              <p>
                We affirm merit-based workplaces that recognize and reward individuals according to
                character, competence, and performance, while upholding fairness and equal
                opportunity for all.
              </p>
            </div>
            <div className="px-5 pb-5 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setFaithModalOpen(false)}
                className="w-full py-2.5 bg-navy text-white rounded-lg font-medium hover:bg-navy-dark transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
